// deno-lint-ignore-file no-unused-vars
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags, Tag, FilterState, ListFilters } from '../types.ts';
import { TasksAPI, TagsAPI, AttachmentsAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';
import { openTagPopup } from './tmk-tag-popup.tsx';
import { UndoManager } from '../undo.ts';
import { renderTagBadge } from './tmk-tag-badge.tsx';
import { mountDescription, renderTaskItem, type TaskItemCallbacks, REORDER_BUTTONS, type ReorderAction } from './tmk-task-item.tsx';
import { renderTaskMeta } from './tmk-task-meta.tsx';
import { appendLinkified } from '../linkify.ts';
import { renderInsertZone, renderPersistentNewTask, type NewTaskRowCallbacks, type InsertTarget } from './tmk-new-task-row.tsx';
import { autoOpenIndex, pendingAfterCreate, type PendingForm } from '../new-task-core.ts';
import { prevVisibleSibling } from '../reorder-core.ts';
import { renderTaskExtras, type ExtrasFocus } from './tmk-task-extras.tsx';

class TmkTaskList extends HTMLElement {
  private tasks: TaskWithTags[] = [];
  private tags: Tag[] = [];
  private mode: 'tree' | 'flat' = 'tree';
  private filters: ListFilters = {};
  private filterState: FilterState | undefined;
  private collapsed = new Set<string>();
  private rootId: string | null = null;
  /** Tryb focus (strona /task/:id): jedno zadanie jako korzeń + jego poddrzewo. */
  private focusTaskId: string | null = null;
  /** Otwarte panele szczegółów wiersza (przeżywają rerender) + fokus do nadania po otwarciu. */
  private openExtras = new Set<string>();
  private extrasFocus = new Map<string, ExtrasFocus>();
  /** Zadania z opisem rozwiniętym w miejscu (spec: Ikony 📝 📎 🔁 → Opis w wierszu). */
  private descExpanded = new Set<string>();
  /** Po utworzeniu zadania: gdzie po rerenderze otworzyć następny formularz (spec: Kolejne zadanie po Enter). */
  private pendingForm: PendingForm | null = null;

  // Multi-select state
  private selectedIds = new Set<string>();
  private selectToolbar: HTMLElement | null = null;

  // Drag state
  private dragId: string | null = null;
  private dragNode: HTMLElement | null = null;
  private dropTarget: { dragId: string; beforeId: string | null; parentId: string | null } | null = null;
  private dropIndicator: HTMLElement | null = null;
  private scrollRAF: number | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragNestDirection: -1 | 0 | 1 = 0;

  // Touch drag
  private touchTimer: number | null = null;
  private touchDragging = false;

  // Loading state
  private loading = false;

  async render(opts: { mode?: string; filters?: ListFilters; filterState?: FilterState; rootId?: string | null; focusTaskId?: string | null } = {}) {
    // Pole niepodane = zachowaj poprzedni stan; render({}) niczego nie resetuje (spec: Undo / Redo — incydent 2026-09-14).
    if (opts.mode !== undefined) this.mode = opts.mode as 'tree' | 'flat';
    if (opts.filters !== undefined) this.filters = opts.filters;
    if (opts.filterState !== undefined) this.filterState = opts.filterState;
    if (opts.rootId !== undefined) this.rootId = opts.rootId;
    if (opts.focusTaskId !== undefined) this.focusTaskId = opts.focusTaskId;

    await this.fetchData();
    this.renderList();
  }

  /** Ponowne pobranie z zapamiętanymi filtrami i trybem — undo/redo i inne odświeżenia z zewnątrz (spec: Undo / Redo). */
  async refresh() {
    await this.rerender();
  }

  private async fetchData() {
    try {
      const filters = { ...this.filters };
      if (this.rootId) filters.root_id = this.rootId;

      if (this.focusTaskId) {
        // Focus: korzeń zawsze widoczny (GET /:id), dzieci przefiltrowane jak w widoku głównym
        // (lista z root_id = zadanie + bieżące filtry).
        const [focused, children, tags] = await Promise.all([
          TasksAPI.getById(this.focusTaskId),
          TasksAPI.list({ ...filters, root_id: this.focusTaskId }),
          TagsAPI.list(),
        ]);
        focused.subtasks = children;
        this.tasks = [focused];
        this.tags = tags;
        this.collapsed.delete(this.focusTaskId);
        return;
      }

      const [tasks, tags] = await Promise.all([
        TasksAPI.list(filters),
        TagsAPI.list(),
      ]);
      this.tasks = tasks;
      this.tags = tags;
    } catch (err: unknown) {
      showToast('Nie udało się załadować zadań: ' + (err as Error).message, 'error');
    }
  }

  private async rerender() {
    if (this.loading) return;
    this.loading = true;
    this.classList.add('tmk-loading');
    try {
      await this.fetchData();
      this.renderList();
    } finally {
      this.loading = false;
      this.classList.remove('tmk-loading');
    }
  }

  private renderList() {
    this.innerHTML = '';
    this.selectToolbar = null;
    if (this.mode === 'flat') {
      this.renderFlat();
    } else {
      this.renderTree();
    }
    if (this.selectedIds.size > 0) this.updateSelectToolbar();
    this.pendingForm = null;   // skonsumowane przez strefę/pole końcowe podczas renderu (albo nieaktualne)
  }

  // ─── Callbacks shared by subcomponents ────────────────────────────────

  private get itemCallbacks(): TaskItemCallbacks {
    return {
      onRerender: () => this.rerender(),
      onOpenTagPopup: (_e, area, task) => openTagPopup(area, task, this.tags, () => this.rerender()),
      onToggleCollapse: (id) => this.toggleCollapse(id),
      onReorder: (task, action) => this.reorderSingle(task, action),
      onToggleCompleted: (task) => this.toggleCompleted(task),
      onToggleCancelled: (task) => this.toggleCancelled(task),
      onToggleExtras: (task, focus) => this.toggleExtras(task, focus),
      isExtrasOpen: (id) => this.openExtras.has(id),
      renderExtras: (task) => this.renderExtrasFor(task),
      isDescExpanded: (id) => this.descExpanded.has(id),
      onDescToggled: (id, expanded) => { if (expanded) this.descExpanded.add(id); else this.descExpanded.delete(id); },
      onDelete: (btn, task) => this.confirmDelete(btn, task),
      onSetupDrag: (grip, id, item) => this.setupDragHandlers(grip, id, item),
      onSetupTouchDrag: (item, id) => this.setupTouchDrag(item, id),
      onSetupItemDragover: (item, id) => this.setupItemDragover(item, id),
      onSetupSelection: (item, id) => this.setupSelection(item, id),
      isSelected: (id) => this.selectedIds.has(id),
    };
  }

  private get newRowCallbacks(): NewTaskRowCallbacks {
    return {
      onRerender: () => this.rerender(),
      getInheritedTagIds: (parentId) => this.getInheritedTagIds(parentId),
      getAllTags: () => this.tags,
      onCreated: (parentId, createdPosition) => { this.pendingForm = pendingAfterCreate(parentId, createdPosition); },
    };
  }

  // ─── TREE MODE ────────────────────────────────────────────────────────

  private collectAllParentIds(tasks: TaskWithTags[]): string[] {
    const ids: string[] = [];
    for (const task of tasks) {
      if (task.subtasks && task.subtasks.length > 0) {
        ids.push(task.id);
        ids.push(...this.collectAllParentIds(task.subtasks as TaskWithTags[]));
      }
    }
    return ids;
  }

  private renderTree() {
    const list = <div className="task-list" /> as HTMLElement;

    // Collapse/expand toolbar
    const hasAnyChildren = this.tasks.some(t => t.subtasks && t.subtasks.length > 0);
    if (hasAnyChildren) {
      const toolbar = <div className="tree-toolbar" /> as HTMLElement;
      const collapseBtn = <button className="btn btn-ghost btn-sm" onClick={() => {
        const allIds = this.collectAllParentIds(this.tasks);
        for (const id of allIds) this.collapsed.add(id);
        this.renderList();
      }}>Zwiń wszystko</button> as HTMLElement;
      const expandBtn = <button className="btn btn-ghost btn-sm" onClick={() => {
        this.collapsed.clear();
        this.renderList();
      }}>Rozwiń wszystko</button> as HTMLElement;
      toolbar.appendChild(collapseBtn);
      toolbar.appendChild(expandBtn);
      list.appendChild(toolbar);
    }

    // Tryb focus: na poziomie korzenia bez stref (dodawałyby rodzeństwo poza widokiem).
    const focusMode = !!this.focusTaskId;

    if (focusMode) {
      for (const task of this.tasks) list.appendChild(this.renderTaskNode(task, 0));
    } else {
      this.appendLevel(list, null, this.tasks, 0, true);
    }

    this.setupListDragover(list);
    this.setupKeyboardShortcuts(list);
    this.appendChild(list);
  }

  private renderTaskNode(task: TaskWithTags, depth: number): HTMLElement {
    const node = <div className="task-node" data-task-id={task.id} /> as HTMLElement;

    const hasChildren = !!(task.subtasks && task.subtasks.length > 0);
    const isFocused = this.focusTaskId === task.id;
    const isCollapsed = !isFocused && this.collapsed.has(task.id);

    node.appendChild(renderTaskItem(task, depth, hasChildren, isCollapsed, this.itemCallbacks));

    // W trybie focus korzeń zawsze ma listę dzieci (ze strefą dodawania), nawet gdy jest liściem.
    if (hasChildren || isFocused) {
      const subtaskList = <div className="subtask-list" /> as HTMLElement;
      if (isCollapsed) subtaskList.style.display = 'none';
      // Strefa końcowa dzieci tylko dla korzenia w trybie focus (pod nim nie ma strefy poziomu wyżej).
      this.appendLevel(subtaskList, task, (task.subtasks ?? []) as TaskWithTags[], depth + 1, isFocused);
      node.appendChild(subtaskList);
    }

    return node;
  }

  /**
   * Jeden poziom drzewa (spec: Tworzenie zadań). Strefa przed każdym wierszem; strefa końcowa tylko
   * tam, gdzie pod poziomem nie następuje żadna strefa poziomu wyżej (korzeń listy; dzieci korzenia
   * w trybie focus) — inaczej dublowałaby się ze strefą pod rodzicem, która i tak oferuje ten cel.
   */
  private appendLevel(container: HTMLElement, parent: TaskWithTags | null, tasks: TaskWithTags[], depth: number, withTrailing: boolean) {
    tasks.forEach((task, i) => {
      const prev = i > 0 ? tasks[i - 1] : null;
      const targets = this.insertTargets(parent, task.position, prev, tasks.length);
      container.appendChild(renderInsertZone(this.filterState, this.newRowCallbacks,
        { targets, depth, autoOpen: autoOpenIndex(targets, this.pendingForm) }));
      container.appendChild(this.renderTaskNode(task, depth));
    });
    if (withTrailing) {
      // Koniec poziomu: strefa (cele „Podzadanie · …” dla łańcucha ostatnich potomków) + stałe pole
      // „Nowe zadanie…” (spec: Stałe pole na końcu), które obsługuje cel poziomu 0 — także jako „następny formularz”.
      const last = tasks.length > 0 ? tasks[tasks.length - 1] : null;
      const targets = this.insertTargets(parent, last ? last.position + 1 : 0, last, tasks.length);
      const auto = autoOpenIndex(targets, this.pendingForm);
      container.appendChild(renderInsertZone(this.filterState, this.newRowCallbacks,
        { targets, depth, autoOpen: auto !== undefined && auto > 0 ? auto : undefined }));
      container.appendChild(renderPersistentNewTask(this.filterState, this.newRowCallbacks, targets[0], depth, auto === 0));
    }
  }

  /**
   * Cele strefy pod wierszem `prev` (spec: Tworzenie zadań → „Cele strefy”): najpierw „Zadanie” na tym
   * poziomie (przed wierszem o pozycji `position`), potem „Podzadanie” dla `prev` i dalej dla łańcucha
   * jego ostatnich rozwiniętych potomków — wszystkie poziomy, które kończą się w tym miejscu.
   */
  private insertTargets(parent: TaskWithTags | null, position: number, prev: TaskWithTags | null, siblingCount: number): InsertTarget[] {
    // Poziom 0 = ten sam poziom co wiersze wokół strefy; „Zadanie” tylko bez rodzica (korzeń listy),
    // pod rodzicem to też „Podzadanie · rodzic” (spec: Tworzenie zadań → nazewnictwo).
    const targets: InsertTarget[] = [{ parentId: parent?.id ?? null, position, level: 0, parentTitle: parent?.title ?? null, siblingCount }];
    let t = prev;
    let k = 1;
    while (t) {
      const children = (t.subtasks ?? []) as TaskWithTags[];
      targets.push({ parentId: t.id, position: undefined, level: k, parentTitle: t.title, siblingCount: children.length });
      const expanded = children.length > 0 && (!this.collapsed.has(t.id) || this.focusTaskId === t.id);
      t = expanded ? children[children.length - 1] : null;
      k++;
    }
    return targets;
  }

  /** Panel załączników do wstawienia w kartę — null, gdy zwinięty. */
  private renderExtrasFor(task: TaskWithTags): HTMLElement | null {
    if (!this.openExtras.has(task.id)) return null;
    const focus = this.extrasFocus.get(task.id) ?? null;
    this.extrasFocus.delete(task.id);
    return renderTaskExtras(task, focus, {
      onClose: () => this.toggleExtras(task, null),
      onChanged: () => this.rerender(),
    });
  }

  // ─── FLAT MODE ────────────────────────────────────────────────────────

  private renderFlat() {
    const list = <div className="task-list" /> as HTMLElement;
    const flatItems = this.flattenTasks(this.tasks, []);

    flatItems.sort((a, b) => {
      if (a.task.deadline && b.task.deadline) return a.task.deadline.localeCompare(b.task.deadline);
      if (a.task.deadline) return -1;
      if (b.task.deadline) return 1;
      return 0;
    });

    for (const { task, breadcrumb } of flatItems) {
      list.appendChild(this.createFlatRow(task, breadcrumb));
    }

    this.appendChild(list);
  }

  private flattenTasks(tasks: TaskWithTags[], parentPath: string[]): { task: TaskWithTags; breadcrumb: string; path: string[] }[] {
    const result: { task: TaskWithTags; breadcrumb: string; path: string[] }[] = [];
    for (const task of tasks) {
      const path = [...parentPath, task.title];
      result.push({ task, breadcrumb: path.join(' > '), path });
      if (task.subtasks && task.subtasks.length > 0) {
        result.push(...this.flattenTasks(task.subtasks as TaskWithTags[], path));
      }
    }
    return result;
  }

  private createFlatRow(task: TaskWithTags, breadcrumb: string): HTMLElement {
    const node = <div className="task-node" data-task-id={task.id} /> as HTMLElement;
    const classes = ['task-item'];
    if (task.completed) classes.push('completed');
    if (task.cancelled) classes.push('cancelled');
    if (this.selectedIds.has(task.id)) classes.push('selected');
    const item = <div className={classes.join(' ')}
      data-task-id={task.id} tabindex="0"
    /> as HTMLElement;

    // Selection (long-press)
    this.setupSelection(item, task.id);

    const checkbox = <input type="checkbox" className="task-checkbox"
      checked={task.completed ? 'checked' : undefined}
      onChange={() => this.toggleCompleted(task)}
    /> as HTMLInputElement;
    item.appendChild(checkbox);

    const body = <div className="task-body" /> as HTMLElement;
    const title = <div className="task-title" /> as HTMLElement;
    const crumbSpan = <span /> as HTMLElement;
    appendLinkified(crumbSpan, breadcrumb);
    title.appendChild(crumbSpan);
    const openLink = <a className="task-open-link" href={'/task/' + task.id}
      onClick={(e: Event) => e.stopPropagation()} title="Otwórz szczegóły"
    >↗</a> as HTMLElement;
    title.appendChild(openLink);
    body.appendChild(title);
    const { edit: editDesc } = mountDescription(task, title, this.itemCallbacks);
    body.appendChild(renderTaskMeta(task, { ...this.itemCallbacks, onEditDescription: editDesc }));
    item.appendChild(body);
    node.appendChild(item);
    return node;
  }

  // ─── COLLAPSE ─────────────────────────────────────────────────────────

  private toggleCollapse(taskId: string) {
    if (this.collapsed.has(taskId)) this.collapsed.delete(taskId);
    else this.collapsed.add(taskId);
    this.renderList();
  }

  // ─── COMPLETED ────────────────────────────────────────────────────────

  private async toggleCompleted(task: TaskWithTags) {
    try {
      UndoManager.push({ type: 'update', taskId: task.id, oldValues: { completed: task.completed } });
      await TasksAPI.update(task.id, { completed: !task.completed });
      task.completed = !task.completed;
      await this.rerender();
    } catch (err: unknown) {
      showToast('Błąd zmiany statusu: ' + (err as Error).message, 'error');
    }
  }

  /** Panel szczegółów wiersza: focus = otwórz i skup na sekcji, null = zwiń. Stan przeżywa rerender. */
  private toggleExtras(task: TaskWithTags, focus: ExtrasFocus) {
    if (focus === null) {
      this.openExtras.delete(task.id);
    } else {
      this.openExtras.add(task.id);
      this.extrasFocus.set(task.id, focus);
    }
    this.renderList();
  }

  /** ⊘ Anuluj / ↺ Przywróć pojedyncze zadanie (spec/lista-zadan.md → Anulowanie zadania). */
  private async toggleCancelled(task: TaskWithTags) {
    try {
      UndoManager.push({ type: 'update', taskId: task.id, oldValues: { cancelled: task.cancelled, completed: task.completed } });
      await TasksAPI.update(task.id, { cancelled: !task.cancelled });
      await this.rerender();
    } catch (err: unknown) {
      showToast('Błąd zmiany statusu: ' + (err as Error).message, 'error');
    }
  }

  // ─── DELETE ───────────────────────────────────────────────────────────

  private confirmDelete(btn: HTMLElement, task: TaskWithTags) {
    if (btn.dataset.confirming) return;
    btn.dataset.confirming = 'true';

    const item = btn.closest('.task-item')!;
    const confirm = <span className="task-delete-confirm" /> as HTMLElement;

    const yesBtn = <button className="task-confirm-yes" onClick={async (e: Event) => {
      e.stopPropagation();
      try {
        UndoManager.push({ type: 'delete', task: JSON.parse(JSON.stringify(task)) });
        await TasksAPI.delete(task.id);
        await this.rerender();
      } catch (err: unknown) {
        showToast('Błąd usuwania: ' + (err as Error).message, 'error');
        confirm.remove();
        delete btn.dataset.confirming;
        btn.style.display = '';
      }
    }}>Usuń</button>;

    const noBtn = <button className="task-confirm-no" onClick={(e: Event) => {
      e.stopPropagation();
      confirm.remove();
      delete btn.dataset.confirming;
      btn.style.display = '';
    }}>Nie</button>;

    confirm.appendChild(yesBtn);
    confirm.appendChild(noBtn);
    btn.style.display = 'none';
    item.appendChild(confirm);
  }

  // ─── TAG POPUP ────────────────────────────────────────────────────────

  // ─── TAG INHERITANCE ──────────────────────────────────────────────────

  private getInheritedTagIds(parentId: string | null): { tag_id: string }[] {
    const tagIds = new Set<string>();

    if (parentId) {
      const parent = this.findTaskById(parentId, this.tasks);
      if (parent?.tags) {
        for (const t of parent.tags) tagIds.add(t.tag_id);
      }
    }

    if (this.filterState?.tags?.length) {
      for (const id of this.filterState.tags) tagIds.add(id);
    }

    return [...tagIds].map(id => ({ tag_id: id }));
  }

  private findTaskById(id: string, tasks: TaskWithTags[]): TaskWithTags | null {
    for (const task of tasks) {
      if (task.id === id) return task;
      if (task.subtasks?.length) {
        const found = this.findTaskById(id, task.subtasks as TaskWithTags[]);
        if (found) return found;
      }
    }
    return null;
  }

  // ─── DRAG & DROP ──────────────────────────────────────────────────────

  private setupDragHandlers(grip: HTMLElement, taskId: string, item: HTMLElement) {
    grip.addEventListener('dragstart', (e: Event) => {
      const de = e as DragEvent;
      this.dragId = taskId;
      this.dragNode = item.closest('.task-node');
      this.dragStartX = de.clientX;
      this.dragStartY = de.clientY;
      this.dragNestDirection = 0;
      de.dataTransfer!.effectAllowed = 'move';
      de.dataTransfer!.setData('text/plain', taskId);
      requestAnimationFrame(() => item.classList.add('dragging'));
    });

    grip.addEventListener('dragend', async () => {
      item.classList.remove('dragging', 'nest-hint-right', 'nest-hint-left');
      this.removeDropIndicator();
      this.stopAutoScroll();

      const nestDir = this.dragNestDirection;
      const target = this.dropTarget;
      this.dropTarget = null;
      this.dragId = null;
      this.dragNode = null;
      this.dragNestDirection = 0;

      if (nestDir !== 0) {
        try {
          const nestParent = nestDir === 1 ? this.visibleIndentParent(taskId) : null;
          if (nestDir === 1 && !nestParent) { showToast('Brak elementu powyżej — nie można zagnieździć', 'info'); await this.rerender(); return; }
          const before = await TasksAPI.getById(taskId);
          if (before) UndoManager.push({ type: 'nest', taskId, oldParentId: before.parent_id, oldPosition: before.position });
          await TasksAPI.nest(taskId, nestDir as -1 | 1, nestParent);
          await this.rerender();
        } catch (err: unknown) {
          showToast('Błąd zagnieżdżenia: ' + (err as Error).message, 'error');
          await this.rerender();
        }
        return;
      }

      if (!target) return;
      try {
        const before = await TasksAPI.getById(target.dragId);
        if (before) UndoManager.push({ type: 'move', taskId: target.dragId, oldBeforeId: null, oldParentId: before.parent_id });
        await TasksAPI.move(target.dragId, target.beforeId, target.parentId);
        await this.rerender();
      } catch (err: unknown) {
        showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
        await this.rerender();
      }
    });
  }

  private setupItemDragover(item: HTMLElement, targetId: string) {
    item.addEventListener('dragover', (e: Event) => {
      const de = e as DragEvent;
      de.preventDefault();
      de.dataTransfer!.dropEffect = 'move';
      if (!this.dragId || this.dragId === targetId) return;
      // Dominująca oś gestu decyduje (spec: D&D → Kierunek gestu): poziomo = indent/outdent
      // (bez wskaźnika dropu), pionowo = reorder.
      if (this.updateDragAxis(de.clientX, de.clientY)) return;
      this.updateDropTarget(de.clientY, item);
      this.handleAutoScroll(de.clientY);
    });
    item.addEventListener('drop', (e: Event) => e.preventDefault());
  }

  private setupListDragover(list: HTMLElement) {
    list.addEventListener('dragover', (e: Event) => {
      const de = e as DragEvent;
      de.preventDefault();
      de.dataTransfer!.dropEffect = 'move';
      if (!this.dragId) return;
      if (this.updateDragAxis(de.clientX, de.clientY)) return;

      const items = list.querySelectorAll('.task-item');
      let closest: HTMLElement | null = null;
      let closestDist = Infinity;
      for (const it of items) {
        if ((it as HTMLElement).dataset.taskId === this.dragId) continue;
        const rect = it.getBoundingClientRect();
        const dist = Math.abs(de.clientY - (rect.top + rect.height / 2));
        if (dist < closestDist) { closestDist = dist; closest = it as HTMLElement; }
      }
      if (closest) this.updateDropTarget(de.clientY, closest);
      this.handleAutoScroll(de.clientY);
    });

    list.addEventListener('dragleave', (e: Event) => {
      const de = e as DragEvent;
      if (!de.relatedTarget || !list.contains(de.relatedTarget as Node)) {
        this.removeDropIndicator();
      }
    });

    list.addEventListener('drop', (e: Event) => e.preventDefault());
  }

  /**
   * Kierunek gestu D&D = dominująca oś (spec/lista-zadan.md → D&D → Kierunek gestu).
   * |dx| > |dy| i |dx| > 24px → tryb poziomy (indent/outdent): wskaźnik dropu znika, przeciągany
   * wiersz dostaje podpowiedź ⇥/⇤ (klasy nest-hint-*). Inaczej tryb pionowy (reorder).
   * Oceniane przy każdym ruchu — decyduje stan w chwili dropu. Zwraca true w trybie poziomym.
   */
  private updateDragAxis(clientX: number, clientY: number): boolean {
    const dx = clientX - this.dragStartX;
    const dy = clientY - this.dragStartY;
    const horizontal = Math.abs(dx) > 24 && Math.abs(dx) > Math.abs(dy);
    this.dragNestDirection = horizontal ? (dx > 0 ? 1 : -1) : 0;
    const item = this.dragNode?.querySelector('.task-item') as HTMLElement | null;
    if (item) {
      item.classList.toggle('nest-hint-right', horizontal && dx > 0);
      item.classList.toggle('nest-hint-left', horizontal && dx < 0);
    }
    if (horizontal) this.removeDropIndicator();
    return horizontal;
  }

  private updateDropTarget(cursorY: number, targetItem: HTMLElement) {
    const targetNode = targetItem.closest('.task-node') as HTMLElement;
    if (!targetNode) return;

    const rect = targetItem.getBoundingClientRect();
    const above = cursorY < rect.top + rect.height / 2;
    const targetId = targetItem.dataset.taskId!;

    let beforeId: string | null;
    if (above) {
      beforeId = targetId;
    } else {
      let next = targetNode.nextElementSibling as HTMLElement | null;
      while (next && (!next.dataset?.taskId || next.classList.contains('task-node-new'))) {
        next = next.nextElementSibling as HTMLElement | null;
      }
      beforeId = next ? next.dataset.taskId! : null;
    }

    const parentNode = targetNode.parentElement?.closest('.task-node') as HTMLElement | null;
    const parentId = parentNode ? parentNode.dataset.taskId! : null;

    this.dropTarget = { dragId: this.dragId!, beforeId, parentId };
    this.showDropIndicator(targetNode, above);
  }

  private showDropIndicator(targetNode: HTMLElement, above: boolean) {
    this.removeDropIndicator();
    const indicator = <div className="drop-indicator" /> as HTMLElement;
    if (above) {
      targetNode.parentNode!.insertBefore(indicator, targetNode);
    } else {
      targetNode.parentNode!.insertBefore(indicator, targetNode.nextSibling);
    }
    this.dropIndicator = indicator;
  }

  private removeDropIndicator() {
    if (this.dropIndicator) {
      this.dropIndicator.remove();
      this.dropIndicator = null;
    }
  }

  // ─── AUTO-SCROLL ──────────────────────────────────────────────────────

  private handleAutoScroll(cursorY: number) {
    const edge = 60;
    const vh = window.innerHeight;
    if (cursorY < edge) this.startAutoScroll(-10);
    else if (cursorY > vh - edge) this.startAutoScroll(10);
    else this.stopAutoScroll();
  }

  private startAutoScroll(speed: number) {
    if (this.scrollRAF) return;
    const tick = () => {
      window.scrollBy(0, speed);
      this.scrollRAF = requestAnimationFrame(tick);
    };
    this.scrollRAF = requestAnimationFrame(tick);
  }

  private stopAutoScroll() {
    if (this.scrollRAF) {
      cancelAnimationFrame(this.scrollRAF);
      this.scrollRAF = null;
    }
  }

  // ─── TOUCH DRAG ───────────────────────────────────────────────────────

  private setupTouchDrag(el: HTMLElement, taskId: string) {
    let startX = 0, startY = 0;
    let dragStartX = 0; // X at moment drag activates
    let nestFired = false; // prevent multiple nest calls per drag

    el.addEventListener('touchstart', (e: Event) => {
      const te = e as TouchEvent;
      const tag = (te.target as HTMLElement).tagName;
      if (['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(tag)) return;

      startX = te.touches[0].clientX;
      startY = te.touches[0].clientY;
      nestFired = false;

      this.touchTimer = window.setTimeout(() => {
        this.touchDragging = true;
        this.dragId = taskId;
        this.dragNode = el.closest('.task-node');
        dragStartX = startX;

        document.body.style.userSelect = 'none';
        (document.body.style as unknown as Record<string, string>).webkitUserSelect = 'none';
        window.getSelection()?.removeAllRanges();

        const item = this.dragNode?.querySelector('.task-item') as HTMLElement | null;
        if (item) item.classList.add('dragging');
        if (navigator.vibrate) navigator.vibrate(30);
      }, 700);
    }, { passive: true });

    el.addEventListener('touchmove', (e: Event) => {
      const te = e as TouchEvent;
      if (!this.touchDragging) {
        if (this.touchTimer) {
          const dx = te.touches[0].clientX - startX;
          const dy = te.touches[0].clientY - startY;
          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
            clearTimeout(this.touchTimer);
            this.touchTimer = null;
          }
        }
        return;
      }
      e.preventDefault();

      const touch = te.touches[0];

      // Poziomy swipe → indent/outdent: dominująca oś (|dx| > |dy|) ponad progiem 40px (spec: D&D → Kierunek gestu)
      if (!nestFired) {
        const hDelta = touch.clientX - dragStartX;
        const vDelta = Math.abs(touch.clientY - startY);
        const absH = Math.abs(hDelta);
        if (absH > 40 && absH > vDelta) {
          nestFired = true;
          this.handleTouchNest(taskId, hDelta > 0 ? 1 : -1);
          return;
        }
      }

      // Vertical movement → reposition
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!target) return;
      const targetItem = (target as HTMLElement).closest('.task-item') as HTMLElement | null;
      if (targetItem && targetItem.dataset.taskId !== this.dragId) {
        this.updateDropTarget(touch.clientY, targetItem);
      }
      this.handleAutoScroll(touch.clientY);
    }, { passive: false });

    const touchEnd = async () => {
      if (this.touchTimer) { clearTimeout(this.touchTimer); this.touchTimer = null; }
      if (!this.touchDragging) return;
      this.touchDragging = false;

      document.body.style.userSelect = '';
      (document.body.style as unknown as Record<string, string>).webkitUserSelect = '';

      const item = this.dragNode?.querySelector('.task-item') as HTMLElement | null;
      if (item) item.classList.remove('dragging');
      this.removeDropIndicator();
      this.stopAutoScroll();

      const target = this.dropTarget;
      this.dropTarget = null;
      this.dragId = null;
      this.dragNode = null;

      if (!target) return;
      try {
        const before = await TasksAPI.getById(target.dragId);
        if (before) UndoManager.push({ type: 'move', taskId: target.dragId, oldBeforeId: null, oldParentId: before.parent_id });
        await TasksAPI.move(target.dragId, target.beforeId, target.parentId);
        await this.rerender();
      } catch (err: unknown) {
        showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
        await this.rerender();
      }
    };

    el.addEventListener('touchend', touchEnd);
    el.addEventListener('touchcancel', touchEnd);
  }

  // ─── TOUCH NEST (horizontal swipe) ─────────────────────────────────────

  private async handleTouchNest(taskId: string, direction: -1 | 1) {
    // Cancel the current drag — we're doing nest instead of move
    this.touchDragging = false;
    document.body.style.userSelect = '';
    (document.body.style as unknown as Record<string, string>).webkitUserSelect = '';

    const item = this.dragNode?.querySelector('.task-item') as HTMLElement | null;
    if (item) item.classList.remove('dragging');
    this.removeDropIndicator();
    this.stopAutoScroll();
    this.dropTarget = null;
    this.dragId = null;
    this.dragNode = null;

    try {
      const nestParent = direction === 1 ? this.visibleIndentParent(taskId) : null;
      if (direction === 1 && !nestParent) { showToast('Brak elementu powyżej — nie można zagnieździć', 'info'); await this.rerender(); return; }
      const before = await TasksAPI.getById(taskId);
      if (before) UndoManager.push({ type: 'nest', taskId, oldParentId: before.parent_id, oldPosition: before.position });
      await TasksAPI.nest(taskId, direction, nestParent);
      if (navigator.vibrate) navigator.vibrate(15);
      await this.rerender();
    } catch (err: unknown) {
      showToast('Błąd zagnieżdżenia: ' + (err as Error).message, 'error');
    }
  }

  // ─── KEYBOARD SHORTCUTS ───────────────────────────────────────────────

  private setupKeyboardShortcuts(list: HTMLElement) {
    list.addEventListener('keydown', async (e: Event) => {
      const ke = e as KeyboardEvent;
      const item = (ke.target as HTMLElement).closest('.task-item') as HTMLElement | null;
      if (!item) return;
      // Don't intercept keys when editing text
      const tag = (ke.target as HTMLElement).tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;

      const taskId = item.dataset.taskId;
      if (!taskId) return;
      const node = item.closest('.task-node') as HTMLElement;
      if (!node) return;

      // Alt+Arrow Up/Down: move task (like VSCode)
      if (ke.altKey && !ke.ctrlKey && !ke.shiftKey && !ke.metaKey && (ke.key === 'ArrowUp' || ke.key === 'ArrowDown')) {
        ke.preventDefault();
        if (ke.key === 'ArrowUp') {
          let prev = node.previousElementSibling as HTMLElement | null;
          while (prev && !prev.dataset?.taskId) prev = prev.previousElementSibling as HTMLElement | null;
          if (prev) {
            try {
              const before = await TasksAPI.getById(taskId);
              if (before) UndoManager.push({ type: 'move', taskId, oldBeforeId: null, oldParentId: before.parent_id });
              await TasksAPI.move(taskId, prev.dataset.taskId!);
              await this.rerenderAndFocus(taskId);
            } catch (err: unknown) {
              showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
            }
          }
        } else {
          let next = node.nextElementSibling as HTMLElement | null;
          while (next && (!next.dataset?.taskId || next.classList.contains('task-node-new')))
            next = next.nextElementSibling as HTMLElement | null;
          if (next) {
            let afterNext = next.nextElementSibling as HTMLElement | null;
            while (afterNext && (!afterNext.dataset?.taskId || afterNext.classList.contains('task-node-new')))
              afterNext = afterNext.nextElementSibling as HTMLElement | null;
            const beforeId = afterNext ? afterNext.dataset.taskId! : null;
            try {
              const before = await TasksAPI.getById(taskId);
              if (before) UndoManager.push({ type: 'move', taskId, oldBeforeId: null, oldParentId: before.parent_id });
              await TasksAPI.move(taskId, beforeId);
              await this.rerenderAndFocus(taskId);
            } catch (err: unknown) {
              showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
            }
          }
        }
        return;
      }

      // Tab: indent, Shift+Tab: outdent
      if (ke.key === 'Tab' && !ke.ctrlKey && !ke.altKey && !ke.metaKey) {
        ke.preventDefault();
        const direction: -1 | 1 = ke.shiftKey ? -1 : 1;
        try {
          const nestParent = direction === 1 ? this.visibleIndentParent(taskId) : null;
          if (direction === 1 && !nestParent) { showToast('Brak elementu powyżej — nie można zagnieździć', 'info'); return; }
          const before = await TasksAPI.getById(taskId);
          if (before) UndoManager.push({ type: 'nest', taskId, oldParentId: before.parent_id, oldPosition: before.position });
          await TasksAPI.nest(taskId, direction, nestParent);
          await this.rerenderAndFocus(taskId);
        } catch (err: unknown) {
          showToast('Błąd zagnieżdżenia: ' + (err as Error).message, 'error');
        }
        return;
      }
    });
  }

  private async rerenderAndFocus(taskId: string) {
    await this.rerender();
    const el = this.querySelector(`.task-item[data-task-id="${taskId}"]`) as HTMLElement | null;
    if (el) el.focus();
  }

  // ─── MULTI-SELECT ─────────────────────────────────────────────────────

  private setupSelection(item: HTMLElement, taskId: string) {
    let timer: number | null = null;
    let moved = false;
    let justSelected = false; // prevent click from undoing long-press

    const startSelect = () => {
      moved = false;
      timer = window.setTimeout(() => {
        timer = null;
        if (!moved) {
          justSelected = true;
          this.toggleSelect(taskId);
          if (navigator.vibrate) navigator.vibrate(30);
        }
      }, 500);
    };

    const cancelSelect = () => {
      if (timer) { clearTimeout(timer); timer = null; }
    };

    const onMove = () => { moved = true; cancelSelect(); };

    // Desktop: mousedown on item body (not grip)
    item.addEventListener('mousedown', (e: Event) => {
      const me = e as MouseEvent;
      if ((me.target as HTMLElement).closest('.task-grip, .task-checkbox, .task-delete-btn, button, a, input, select')) return;
      startSelect();
    });
    item.addEventListener('mouseup', cancelSelect);
    item.addEventListener('mousemove', onMove);

    // Mobile: touchstart
    item.addEventListener('touchstart', (e: Event) => {
      const te = e as TouchEvent;
      if (['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA', 'A'].includes((te.target as HTMLElement).tagName)) return;
      if ((te.target as HTMLElement).closest('.task-grip, .task-checkbox, .task-delete-btn')) return;
      startSelect();
    }, { passive: true });
    item.addEventListener('touchend', cancelSelect, { passive: true });
    item.addEventListener('touchmove', onMove, { passive: true });

    // In select mode, simple click toggles selection (but skip if long-press just fired)
    item.addEventListener('click', (e: Event) => {
      if (justSelected) { justSelected = false; return; }
      if (this.selectedIds.size === 0) return;
      const el = e.target as HTMLElement;
      if (el.closest('.task-checkbox, .task-delete-btn, button, a, input, select, .task-grip')) return;
      e.preventDefault();
      e.stopPropagation();
      this.toggleSelect(taskId);
    });
  }

  private toggleSelect(taskId: string) {
    if (this.selectedIds.has(taskId)) {
      this.selectedIds.delete(taskId);
    } else {
      this.selectedIds.add(taskId);
    }
    // Update item visual
    const item = this.querySelector(`.task-item[data-task-id="${taskId}"]`);
    if (item) item.classList.toggle('selected', this.selectedIds.has(taskId));
    this.updateSelectToolbar();
  }

  /** Wklejone ze schowka obrazy → załączniki zadania (spec/zadania.md → Załączniki → Wklejanie ze schowka). */
  async pasteFiles(taskId: string, files: File[]) {
    const stamp = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
    let ok = 0;
    for (const [i, f] of files.entries()) {
      const ext = (f.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
      const named = new File([f], `wklejone-${stamp}${files.length > 1 ? '-' + (i + 1) : ''}.${ext}`, { type: f.type });
      try {
        await AttachmentsAPI.upload(taskId, named);
        ok++;
      } catch (err: unknown) {
        showToast('Błąd wklejania obrazu: ' + (err as Error).message, 'error');
      }
    }
    if (ok > 0) {
      showToast(ok === 1 ? 'Obraz dodany jako załącznik' : `Dodano ${ok} obrazy jako załączniki`, 'success');
      this.openExtras.add(taskId);
      this.extrasFocus.set(taskId, 'attachments');
      await this.rerender();
    }
  }

  /** Ctrl+A: zaznacz wszystkie WIDOCZNE zadania (zwinięte poddrzewa pomijane) — spec: multi-select. */
  selectAll() {
    const items = [...this.querySelectorAll<HTMLElement>('.task-item[data-task-id]')]
      .filter((el) => !el.closest('.subtask-list[style*="display: none"]'));
    if (items.length === 0) return;
    this.selectedIds = new Set(items.map((el) => el.dataset.taskId!));
    this.renderList();
  }

  hasSelection(): boolean {
    return this.selectedIds.size > 0;
  }

  clearSelectionPublic() {
    this.clearSelection();
  }

  private clearSelection() {
    for (const id of this.selectedIds) {
      const item = this.querySelector(`.task-item[data-task-id="${id}"]`);
      if (item) item.classList.remove('selected');
    }
    this.selectedIds.clear();
    this.updateSelectToolbar();
  }

  private updateSelectToolbar() {
    if (this.selectedIds.size === 0) {
      if (this.selectToolbar) { this.selectToolbar.remove(); this.selectToolbar = null; }
      return;
    }
    if (!this.selectToolbar) {
      this.selectToolbar = this.buildSelectToolbar();
      this.prepend(this.selectToolbar);
    }
    const count = this.selectToolbar.querySelector('.select-count');
    if (count) count.textContent = `Zaznaczono: ${this.selectedIds.size}`;
  }

  private buildSelectToolbar(): HTMLElement {
    const bar = <div className="select-toolbar" /> as HTMLElement;

    const count = <span className="select-count">Zaznaczono: {this.selectedIds.size}</span> as HTMLElement;
    bar.appendChild(count);

    const actions = <div className="select-actions" /> as HTMLElement;

    // Ukończ / Cofnij ukończenie
    const completeBtn = <button className="btn btn-ghost btn-sm" title="Ukończ/Cofnij"
      onClick={() => this.batchToggleCompleted()}>✓</button> as HTMLElement;
    actions.appendChild(completeBtn);

    // Anuluj / Przywróć (wg większości zaznaczonych)
    const cancelTaskBtn = <button className="btn btn-ghost btn-sm" title="Anuluj / przywróć zaznaczone"
      onClick={() => this.batchToggleCancelled()}>⊘</button> as HTMLElement;
    actions.appendChild(cancelTaskBtn);

    // Deadline
    const deadlineBtn = <button className="btn btn-ghost btn-sm" title="Ustaw deadline"
      onClick={() => this.batchSetDeadline()}>📅</button> as HTMLElement;
    actions.appendChild(deadlineBtn);

    // Przypisz
    const assignBtn = <button className="btn btn-ghost btn-sm" title="Przypisz osobę"
      onClick={() => this.batchAssign()}>👤</button> as HTMLElement;
    actions.appendChild(assignBtn);

    // Tagi
    const tagBtn = <button className="btn btn-ghost btn-sm" title="Dodaj tagi"
      onClick={() => this.batchAddTags()}>🏷</button> as HTMLElement;
    actions.appendChild(tagBtn);

    // Przesuwanie w strukturze: ⤒ ⤓ ⇥ ⇤ (spec: "Przyciski przesuwania")
    for (const [action, icon, title] of REORDER_BUTTONS) {
      actions.appendChild(<button className="btn btn-ghost btn-sm" title={title}
        onClick={() => this.batchReorder(action)}>{icon}</button> as HTMLElement);
    }

    // Przesuń
    const moveBtn = <button className="btn btn-ghost btn-sm" title="Przenieś do..."
      onClick={() => this.batchMove()}>↕</button> as HTMLElement;
    actions.appendChild(moveBtn);

    // Usuń
    const deleteBtn = <button className="btn btn-ghost btn-sm btn-danger" title="Usuń zaznaczone"
      onClick={() => this.batchDelete()}>✕</button> as HTMLElement;
    actions.appendChild(deleteBtn);

    // Anuluj
    const cancelBtn = <button className="btn btn-ghost btn-sm" title="Anuluj zaznaczenie"
      onClick={() => this.clearSelection()}>Anuluj</button> as HTMLElement;
    actions.appendChild(cancelBtn);

    bar.appendChild(actions);
    return bar;
  }

  // ─── PRZESUWANIE PRZYCISKAMI (spec/lista-zadan.md "Przyciski przesuwania") ───

  /** Id WIDOCZNYCH zadań na poziomie `taskId` w kolejności DOM (strefy wstawiania i pole końcowe pomijane). */
  private visibleSiblingIds(taskId: string): string[] {
    const node = this.querySelector(`.task-node[data-task-id="${taskId}"]`);
    const container = node?.parentElement;
    if (!container) return [];
    return [...container.children].map((el) => (el as HTMLElement).dataset.taskId ?? '').filter(Boolean);
  }

  /**
   * Rodzic dla „poziom niżej” = poprzednie WIDOCZNE rodzeństwo (spec/lista-zadan.md → Przyciski przesuwania).
   * Serwer nie zna filtrów widoku — licząc po pozycji w bazie wsuwał zadania pod ukryte, ukończone
   * sąsiedztwo (incydent 2026-09-11). null = brak widocznego sąsiada powyżej → nie wołamy API.
   */
  private visibleIndentParent(taskId: string, exclude: Iterable<string> = []): string | null {
    return prevVisibleSibling(this.visibleSiblingIds(taskId), taskId, exclude);
  }

  private skippedMessage(action: ReorderAction, n: number): string {
    const why = action === 'indent' ? 'brak elementu powyżej'
      : action === 'outdent' ? 'już na najwyższym poziomie'
      : 'bez zmian';
    return n === 1 ? `Pominięto 1 — ${why}` : `Pominięto ${n} — ${why}`;
  }

  /** Pojedyncze zadanie: jak skrót klawiszowy — undo + focus wraca na element. */
  private async reorderSingle(task: TaskWithTags, action: ReorderAction) {
    try {
      let payload: Record<string, unknown> = {};
      if (action === 'indent') {
        const parentId = this.visibleIndentParent(task.id);
        if (!parentId) { showToast('Brak elementu powyżej — nie można zagnieździć', 'info'); return; }
        payload = { parents: { [task.id]: parentId } };
      }
      const res = await TasksAPI.batch([task.id], action, payload);
      if (res.skipped) {
        showToast(action === 'indent' ? 'Brak elementu powyżej — nie można zagnieździć' : 'Zadanie jest już na najwyższym poziomie', 'info');
        return;
      }
      if (action === 'indent' || action === 'outdent') {
        UndoManager.push({ type: 'nest', taskId: task.id, oldParentId: task.parent_id, oldPosition: task.position });
      } else {
        UndoManager.push({ type: 'move', taskId: task.id, oldBeforeId: null, oldParentId: task.parent_id });
      }
      await this.rerenderAndFocus(task.id);
    } catch (err: unknown) {
      showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
    }
  }

  /** Wiele zadań: jedna transakcja po stronie serwera, zaznaczenie zostaje. */
  private async batchReorder(action: ReorderAction) {
    const ids = [...this.selectedIds];
    if (ids.length === 0) return;
    try {
      const payload: Record<string, unknown> = {};
      if (action === 'indent') {
        // Każde zaznaczone pod swojego WIDOCZNEGO sąsiada powyżej (z pominięciem innych zaznaczonych — kolejność zostaje).
        const parents: Record<string, string> = {};
        for (const id of ids) { const p = this.visibleIndentParent(id, ids); if (p) parents[id] = p; }
        payload.parents = parents;
      }
      const res = await TasksAPI.batch(ids, action, payload);
      await this.rerender();
      if (res.skipped) showToast(this.skippedMessage(action, res.skipped), 'info');
    } catch (err: unknown) {
      showToast('Błąd przenoszenia: ' + (err as Error).message, 'error');
    }
  }

  // ─── BATCH OPERATIONS ────────────────────────────────────────────────

  private async batchToggleCompleted() {
    const ids = [...this.selectedIds];
    // Check majority: if most are incomplete, complete them; otherwise uncomplete
    const tasks = ids.map(id => this.findTaskById(id, this.tasks)).filter(Boolean);
    const mostCompleted = tasks.filter(t => t!.completed).length > tasks.length / 2;
    try {
      await TasksAPI.batch(ids, 'update', { completed: !mostCompleted });
      this.selectedIds.clear();
      await this.rerender();
      showToast(mostCompleted ? 'Cofnięto ukończenie' : 'Oznaczono jako ukończone', 'success');
    } catch (err: unknown) {
      showToast('Błąd: ' + (err as Error).message, 'error');
    }
  }

  private async batchToggleCancelled() {
    const ids = [...this.selectedIds];
    const tasks = ids.map(id => this.findTaskById(id, this.tasks)).filter(Boolean);
    const mostCancelled = tasks.filter(t => t!.cancelled).length > tasks.length / 2;
    try {
      await TasksAPI.batch(ids, 'update', { cancelled: !mostCancelled });
      this.selectedIds.clear();
      await this.rerender();
      showToast(mostCancelled ? 'Przywrócono zadania' : 'Anulowano zadania', 'success');
    } catch (err: unknown) {
      showToast('Błąd: ' + (err as Error).message, 'error');
    }
  }

  private async batchSetDeadline() {
    const date = prompt('Deadline (YYYY-MM-DD) lub puste aby usunąć:');
    if (date === null) return; // cancelled
    try {
      await TasksAPI.batch([...this.selectedIds], 'update', { deadline: date || null });
      this.selectedIds.clear();
      await this.rerender();
      showToast('Deadline ustawiony', 'success');
    } catch (err: unknown) {
      showToast('Błąd: ' + (err as Error).message, 'error');
    }
  }

  private async batchAssign() {
    const email = prompt('Email osoby (lub puste aby usunąć):');
    if (email === null) return;
    try {
      await TasksAPI.batch([...this.selectedIds], 'update', { assigned_email: email || null });
      this.selectedIds.clear();
      await this.rerender();
      showToast('Przypisano', 'success');
    } catch (err: unknown) {
      showToast('Błąd: ' + (err as Error).message, 'error');
    }
  }

  private async batchAddTags() {
    // Show tag picker popup anchored to toolbar
    if (!this.selectToolbar) return;
    const ids = [...this.selectedIds];

    // Build a simple tag picker
    const existing = this.selectToolbar.querySelector('.batch-tag-picker');
    if (existing) { existing.remove(); return; }

    const picker = <div className="batch-tag-picker" /> as HTMLElement;
    for (const tag of this.tags) {
      const btn = <button className="tag-badge" style={{ backgroundColor: tag.color, color: '#fff' }}
        onClick={async () => {
          try {
            await TasksAPI.batch(ids, 'add_tags', { tag_ids: [tag.id] });
            picker.remove();
            this.selectedIds.clear();
            await this.rerender();
            showToast(`Tag "${tag.name}" dodany`, 'success');
          } catch (err: unknown) {
            showToast('Błąd: ' + (err as Error).message, 'error');
          }
        }}>{tag.name}</button> as HTMLElement;
      picker.appendChild(btn);
    }
    this.selectToolbar.appendChild(picker);
  }

  /** Picker „Przenieś do…” — panel z wyszukiwarką i listą celów (spec/lista-zadan.md, multi-select). */
  private batchMove() {
    if (!this.selectToolbar) return;
    const ids = [...this.selectedIds];

    const existing = this.selectToolbar.querySelector('.batch-move-picker');
    if (existing) { existing.remove(); return; }

    const picker = <div className="batch-move-picker" role="dialog" aria-label="Przenieś do" /> as HTMLElement;
    const input = <input type="text" className="batch-move-search" placeholder="Szukaj zadania docelowego..." /> as HTMLInputElement;
    const results = <div className="batch-move-results" role="listbox" /> as HTMLElement;
    picker.appendChild(input);
    picker.appendChild(results);

    // Zamknięcie: wybór, ponowne ↕ (toggle wyżej), Escape, klik/tap poza panelem.
    const cleanup = () => {
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
    };
    const close = () => { picker.remove(); cleanup(); };
    const onOutside = (e: Event) => {
      if (!picker.isConnected) { cleanup(); return; }
      const target = e.target as Node;
      if (picker.contains(target)) return;
      if (this.selectToolbar?.querySelector('.select-actions')?.contains(target)) return; // ↕ sam toggluje
      close();
    };
    const onKey = (e: Event) => {
      if (!picker.isConnected) { cleanup(); return; }
      if ((e as KeyboardEvent).key === 'Escape') close();
    };
    document.addEventListener('pointerdown', onOutside, true);
    document.addEventListener('keydown', onKey, true);

    const moveTo = async (parentId: string | null, label: string) => {
      try {
        await TasksAPI.batch(ids, 'move', { parent_id: parentId });
        close();
        this.selectedIds.clear();
        await this.rerender();
        showToast(parentId ? `Przeniesiono pod „${label}”` : 'Przeniesiono na najwyższy poziom', 'success');
      } catch (err: unknown) { showToast('Błąd: ' + (err as Error).message, 'error'); }
    };

    const option = (parentPath: string[], leaf: string, onPick: () => void, icon?: string): HTMLElement => {
      const btn = <button type="button" className="batch-move-option" role="option" onClick={onPick} /> as HTMLElement;
      if (icon) btn.appendChild(<span className="batch-move-icon">{icon}</span> as HTMLElement);
      if (parentPath.length > 0) {
        btn.appendChild(<span className="batch-move-path">{parentPath.join(' › ') + ' ›'}</span> as HTMLElement);
      }
      btn.appendChild(<span className="batch-move-leaf">{leaf}</span> as HTMLElement);
      btn.title = [...parentPath, leaf].join(' > ');
      return btn;
    };

    // Cele z serwera (spec/lista-zadan.md → Picker): GET /api/tasks/search — bez ciągnięcia całego
    // drzewa do przeglądarki; serwer wyklucza zaznaczone i ich poddrzewa. Debounce 200 ms,
    // odpowiedzi spóźnione (starszy numer sekwencji) są ignorowane.
    const MAX_RESULTS = 15;
    let hits: { id: string; title: string; path: string[] }[] = [];
    let status: 'loading' | 'ready' | 'error' = 'loading';
    let errorMsg = '';
    let seq = 0;
    let timer: number | null = null;

    const renderResults = () => {
      results.textContent = '';
      results.appendChild(option([], 'Najwyższy poziom (root)', () => moveTo(null, ''), '📁'));
      if (status === 'loading') {
        results.appendChild(<div className="batch-move-hint">Szukam…</div> as HTMLElement);
        return;
      }
      if (status === 'error') {
        results.appendChild(<div className="batch-move-hint">{errorMsg}</div> as HTMLElement);
        return;
      }
      for (const hit of hits) {
        results.appendChild(option(hit.path, hit.title, () => moveTo(hit.id, hit.title)));
      }
      const q = input.value.trim();
      if (hits.length === 0) {
        results.appendChild(<div className="batch-move-hint">{q ? 'Brak zadań pasujących do „' + q + '”' : 'Brak zadań'}</div> as HTMLElement);
      } else if (hits.length >= MAX_RESULTS) {
        results.appendChild(<div className="batch-move-hint">{`Pokazano pierwsze ${MAX_RESULTS} — zawęź wyszukiwanie`}</div> as HTMLElement);
      }
    };

    const search = async () => {
      const my = ++seq;
      status = 'loading';
      renderResults();
      try {
        const res = await TasksAPI.searchTargets(input.value, ids, MAX_RESULTS);
        if (my !== seq || !picker.isConnected) return;
        hits = res;
        status = 'ready';
      } catch (err: unknown) {
        if (my !== seq) return;
        status = 'error';
        errorMsg = 'Błąd wyszukiwania: ' + (err as Error).message;
      }
      renderResults();
    };

    search();
    input.addEventListener('input', () => {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(search, 200);
    });
    this.selectToolbar.appendChild(picker);

    // Autofocus tylko z myszą — na dotyku klawiatura zasłoniłaby listę.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) input.focus();
  }

  private async batchDelete() {
    const count = this.selectedIds.size;
    if (!confirm(`Usunąć ${count} zadań?`)) return;
    try {
      await TasksAPI.batch([...this.selectedIds], 'delete');
      this.selectedIds.clear();
      await this.rerender();
      showToast(`Usunięto ${count} zadań`, 'success');
    } catch (err: unknown) {
      showToast('Błąd: ' + (err as Error).message, 'error');
    }
  }
}

customElements.define('tmk-task-list', TmkTaskList);

// Wklejanie obrazów ze schowka (spec/zadania.md → Załączniki → Wklejanie ze schowka): Ctrl+V z fokusem
// na wierszu zadania (lub w jego panelu / polu) → obrazy trafiają do tego zadania jako załączniki.
// Wklejanie tekstu nie jest ruszane.
document.addEventListener('paste', (e: ClipboardEvent) => {
  const list = document.querySelector('tmk-task-list') as TmkTaskList | null;
  if (!list || !list.isConnected) return;
  const files = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
  if (files.length === 0) return;
  const from = (e.target as HTMLElement | null) ?? (document.activeElement as HTMLElement | null);
  const active = document.activeElement as HTMLElement | null;
  const host = from?.closest?.('.task-item[data-task-id]') ?? active?.closest?.('.task-item[data-task-id]');
  const taskId = (host as HTMLElement | null)?.dataset.taskId;
  if (!taskId) {
    showToast('Kliknij zadanie, do którego wkleić obraz, i wciśnij Ctrl+V ponownie', 'info');
    return;
  }
  e.preventDefault();
  list.pasteFiles(taskId, files);
});

// Skróty globalne multi-select (spec/lista-zadan.md → Zaznaczanie wielu): Ctrl/⌘+A poza polami
// tekstowymi zaznacza wszystkie widoczne zadania bieżącej listy, Escape czyści zaznaczenie.
// Listener na document, bo lista jest tworzona od nowa przy każdym renderze widoku.
document.addEventListener('keydown', (e: KeyboardEvent) => {
  const target = e.target as HTMLElement | null;
  const tag = target?.tagName ?? '';
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || target?.isContentEditable) return;
  const list = document.querySelector('tmk-task-list') as TmkTaskList | null;
  if (!list || !list.isConnected) return;
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'a') {
    e.preventDefault();
    list.selectAll();
  } else if (e.key === 'Escape' && list.hasSelection()) {
    e.preventDefault();
    list.clearSelectionPublic();
  }
});

export { TmkTaskList };
